import { notify } from '@/stores/notificationStore';
import i18n from '@/i18n';

/**
 * Système de notifications centralisé
 * Utilise Zustand notify en interne + i18n pour les traductions
 *
 * @example
 * ```typescript
 * import { notifications, showSuccess, showError } from '@/lib/notifications';
 *
 * // Méthodes simples
 * showSuccess("Opération réussie");
 * showError("Une erreur est survenue", "Erreur");
 *
 * // Helpers métier
 * notifications.created("Client");
 * notifications.updateError("la facture");
 * ```
 */

const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, options);

// ==================== API de base ====================

/**
 * Afficher une notification de succès
 */
export const showSuccess = (message: string, title?: string, duration = 5000) => {
  notify.success(message, title, duration);
};

/**
 * Afficher une notification d'erreur
 */
export const showError = (message: string, title?: string, duration = 7000) => {
  notify.error(message, title, duration);
};

/**
 * Afficher une notification d'avertissement
 */
export const showWarning = (message: string, title?: string, duration = 6000) => {
  notify.warning(message, title, duration);
};

/**
 * Afficher une notification d'information
 */
export const showInfo = (message: string, title?: string, duration = 5000) => {
  notify.info(message, title, duration);
};

// ==================== Helpers Métier ====================

/**
 * Helpers spécifiques pour opérations CRUD et cas d'usage métier
 */
export const notifications = {
  // ==================== CRUD Operations ====================

  /**
   * Afficher notification de création réussie
   * @example notifications.created("Client")
   */
  created: (entity: string, title?: string) => {
    showSuccess(
      t('notifications.crud.created', { entity }),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher notification de mise à jour réussie
   * @example notifications.updated("Facture")
   */
  updated: (entity: string, title?: string) => {
    showSuccess(
      t('notifications.crud.updated', { entity }),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher notification de suppression réussie
   * @example notifications.deleted("le client")
   */
  deleted: (entity: string, title?: string) => {
    showSuccess(
      t('notifications.crud.deleted', { entity }),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher notification de sauvegarde réussie
   * @example notifications.saved("Les paramètres")
   */
  saved: (entity?: string, title?: string) => {
    showSuccess(
      t('notifications.crud.saved', { entity: entity || t('common.labels.description') }),
      title || t('notifications.titles.success')
    );
  },

  // ==================== CRUD Errors ====================

  /**
   * Afficher erreur de création
   * @example notifications.createError("le client")
   */
  createError: (entity: string, title?: string) => {
    showError(
      t('notifications.crud.createError', { entity }),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher erreur de mise à jour
   * @example notifications.updateError("la facture")
   */
  updateError: (entity: string, title?: string) => {
    showError(
      t('notifications.crud.updateError', { entity }),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher erreur de suppression
   * @example notifications.deleteError("le produit")
   */
  deleteError: (entity: string, title?: string) => {
    showError(
      t('notifications.crud.deleteError', { entity }),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher erreur de chargement
   * @example notifications.loadError("les données")
   */
  loadError: (entity: string, title?: string) => {
    showError(
      t('notifications.crud.loadError', { entity }),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher erreur de sauvegarde
   * @example notifications.saveError("les paramètres")
   */
  saveError: (entity?: string, title?: string) => {
    showError(
      t('notifications.crud.saveError', { entity: entity || t('common.labels.description') }),
      title || t('notifications.titles.error')
    );
  },

  // ==================== Network & API ====================

  /**
   * Afficher erreur de connexion réseau
   */
  networkError: (title?: string) => {
    showError(
      t('notifications.network.connectionError'),
      title || t('notifications.titles.error'),
      8000
    );
  },

  /**
   * Afficher erreur serveur
   */
  serverError: (title?: string) => {
    showError(
      t('notifications.network.serverError'),
      title || t('notifications.titles.error'),
      8000
    );
  },

  /**
   * Afficher erreur d'API générique
   */
  apiError: (message?: string, title?: string) => {
    showError(
      message || t('notifications.network.apiError'),
      title || t('notifications.titles.error'),
      8000
    );
  },

  // ==================== Validation ====================

  /**
   * Afficher avertissement champs obligatoires
   */
  requiredFields: (title?: string) => {
    showWarning(
      t('notifications.validation.requiredFields'),
      title || t('notifications.titles.warning')
    );
  },

  /**
   * Afficher erreur de format
   * @example notifications.invalidFormat("l'email")
   */
  invalidFormat: (field: string, title?: string) => {
    showWarning(
      t('notifications.validation.invalidFormat', { field }),
      title || t('notifications.titles.warning')
    );
  },

  /**
   * Afficher erreur de validation générique
   */
  validationError: (message: string, title?: string) => {
    showWarning(message, title || t('notifications.titles.warning'));
  },

  // ==================== Permissions & Auth ====================

  /**
   * Afficher erreur d'autorisation
   */
  unauthorized: (title?: string) => {
    showError(
      t('notifications.auth.unauthorized'),
      title || t('notifications.titles.error'),
      7000
    );
  },

  /**
   * Afficher erreur de session expirée
   */
  sessionExpired: (title?: string) => {
    showWarning(
      t('notifications.auth.sessionExpired'),
      title || t('notifications.titles.warning'),
      8000
    );
  },

  // ==================== Import/Export ====================

  /**
   * Afficher succès d'import
   * @example notifications.importSuccess(10)
   */
  importSuccess: (count: number, entity = "enregistrement(s)", title?: string) => {
    showSuccess(
      t('notifications.import.success', { count, entity }),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher succès d'import partiel
   * @example notifications.importPartial(8, 2)
   */
  importPartial: (success: number, failed: number, title?: string) => {
    showWarning(
      t('notifications.import.partial', { success, failed }),
      title || t('notifications.titles.warning'),
      7000
    );
  },

  /**
   * Afficher erreur d'import
   */
  importError: (message?: string, title?: string) => {
    showError(
      message || t('notifications.import.error'),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher succès d'export
   */
  exportSuccess: (title?: string) => {
    showSuccess(
      t('notifications.export.success'),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher erreur d'export
   */
  exportError: (title?: string) => {
    showError(
      t('notifications.export.error'),
      title || t('notifications.titles.error')
    );
  },

  // ==================== Clipboard ====================

  /**
   * Afficher notification de copie
   */
  copied: (title?: string) => {
    showInfo(
      t('notifications.clipboard.copied'),
      title || t('notifications.titles.info'),
      3000
    );
  },

  // ==================== Email ====================

  /**
   * Afficher succès d'envoi d'email
   */
  emailSent: (recipient?: string, title?: string) => {
    const message = recipient
      ? t('notifications.email.sentTo', { recipient })
      : t('notifications.email.sent');
    showSuccess(message, title || t('notifications.titles.success'));
  },

  /**
   * Afficher erreur d'envoi d'email
   */
  emailError: (title?: string) => {
    showError(
      t('notifications.email.error'),
      title || t('notifications.titles.error')
    );
  },

  // ==================== Quota & Limits ====================

  /**
   * Afficher erreur de quota atteint
   */
  quotaReached: (resource: string, title?: string) => {
    showError(
      t('notifications.quota.reached', { resource }),
      title || t('notifications.titles.error'),
      8000
    );
  },

  // ==================== Upload/Download ====================

  /**
   * Afficher succès d'upload
   */
  uploadSuccess: (fileName?: string, title?: string) => {
    const message = fileName
      ? t('notifications.upload.successNamed', { fileName })
      : t('notifications.upload.success');
    showSuccess(message, title || t('notifications.titles.success'));
  },

  /**
   * Afficher erreur d'upload
   */
  uploadError: (message?: string, title?: string) => {
    showError(
      message || t('notifications.upload.error'),
      title || t('notifications.titles.error')
    );
  },

  /**
   * Afficher succès de téléchargement
   */
  downloadSuccess: (fileName?: string, title?: string) => {
    const message = fileName
      ? t('notifications.download.successNamed', { fileName })
      : t('notifications.download.success');
    showSuccess(message, title || t('notifications.titles.success'));
  },

  // ==================== Payment & Billing ====================

  /**
   * Afficher succès de paiement
   */
  paymentSuccess: (title?: string) => {
    showSuccess(
      t('notifications.payment.success'),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher erreur de paiement
   */
  paymentError: (message?: string, title?: string) => {
    showError(
      message || t('notifications.payment.error'),
      title || t('notifications.titles.error')
    );
  },

  // ==================== Generic ====================

  /**
   * Afficher notification générique de succès
   */
  success: (message: string, title?: string) => {
    showSuccess(message, title || t('notifications.titles.success'));
  },

  /**
   * Afficher notification générique d'erreur
   */
  error: (message: string, title?: string) => {
    showError(message, title || t('notifications.titles.error'));
  },

  /**
   * Afficher notification générique d'avertissement
   */
  warning: (message: string, title?: string) => {
    showWarning(message, title || t('notifications.titles.warning'));
  },

  /**
   * Afficher notification générique d'information
   */
  info: (message: string, title?: string) => {
    showInfo(message, title);
  },

  // ==================== Confirmation Actions ====================

  /**
   * Afficher confirmation d'action
   */
  actionConfirmed: (action?: string, title?: string) => {
    showSuccess(
      t('notifications.confirmation.actionConfirmed', { action: action || t('common.labels.actions') }),
      title || t('notifications.titles.success')
    );
  },

  /**
   * Afficher annulation d'action
   */
  actionCancelled: (action?: string, title?: string) => {
    showInfo(
      t('notifications.confirmation.actionCancelled', { action: action || t('common.labels.actions') }),
      title || t('notifications.titles.info'),
      4000
    );
  },
};

/**
 * Export par défaut pour usage simplifié
 */
export default {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  ...notifications,
};
